import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { useToast } from '../../components/ui/use-toast';
import { confirm } from '../../components/ui/confirm-dialog';
import { useRestaurant } from '../../context/RestaurantContext';
import {
  ChevronLeft, ScanLine, Search, CheckCircle2, Circle, X, FileText, Package,
} from 'lucide-react';
import BarcodeScanner from '../../components/BarcodeScanner';
import {
  getStocktake, scanCount, unscanCount, completeStocktake, lookupBarcode,
} from '../../services/liquorTrackingService';

const fmtMl = (n) => `${Math.round(Number(n || 0))} ml`;
const bottlesText = (full, openMl) => {
  const parts = [];
  parts.push(`${full || 0} full`);
  if (Number(openMl) > 0) parts.push(`${Math.round(Number(openMl))} ml open`);
  return parts.join(' · ');
};

export default function StocktakeWorkspace() {
  const navigate = useNavigate();
  const { restaurantId: urlRestaurantId, locationId, stocktakeId } = useParams();
  const { restaurantId: ctxRestaurantId } = useRestaurant();
  const restaurantId = urlRestaurantId || ctxRestaurantId;
  const { toast } = useToast();

  const [data, setData] = useState(null); // { stocktake, expected, scanned, remaining }
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [counting, setCounting] = useState(null); // { dish, counted_full_bottles, counted_open_ml, method }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId || !locationId || !stocktakeId) return;
    setLoading(true);
    try {
      setData(await getStocktake(restaurantId, locationId, stocktakeId));
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to load stocktake', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [restaurantId, locationId, stocktakeId, toast]);

  useEffect(() => { load(); }, [load]);

  const st = data?.stocktake;
  const completed = st?.status === 'completed';

  const expected = useMemo(() => data?.expected || [], [data]);
  const scanned = useMemo(() => data?.scanned || [], [data]);
  const remaining = useMemo(() => data?.remaining || [], [data]);

  const filteredExpected = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return expected;
    return expected.filter((e) => `${e.name} ${e.brand}`.toLowerCase().includes(q));
  }, [expected, search]);

  const openCount = useCallback((item) => {
    // Seed the form from an existing scanned line if there is one.
    const existing = scanned.find((s) => s.dish_id === item.dish_id);
    setCounting({
      dish_id: item.dish_id,
      name: item.name,
      brand: item.brand,
      bottle_size_ml: item.bottle_size_ml,
      system_full_bottles: item.system_full_bottles ?? existing?.system_full_bottles ?? 0,
      system_open_ml: item.system_open_ml ?? existing?.system_open_ml ?? 0,
      counted_full_bottles: existing ? String(existing.counted_full_bottles) : '',
      counted_open_ml: existing ? String(existing.counted_open_ml) : '',
      method: 'manual',
    });
  }, [scanned]);

  const submitCount = async () => {
    setSaving(true);
    try {
      await scanCount(restaurantId, locationId, stocktakeId, {
        dish_id: counting.dish_id,
        counted_full_bottles: Number(counting.counted_full_bottles || 0),
        counted_open_ml: Number(counting.counted_open_ml || 0),
        method: counting.method || 'manual',
      });
      setCounting(null);
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to save count', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const removeScan = async (dishId) => {
    try {
      await unscanCount(restaurantId, locationId, stocktakeId, dishId);
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to remove count', variant: 'destructive' });
    }
  };

  const onBarcode = useCallback(async (code) => {
    try {
      const res = await lookupBarcode(restaurantId, locationId, stocktakeId, code);
      if (res?.found && res.data) {
        // Find the expected row for richer system on-hand, then open the counter.
        const item = (data?.expected || []).find((e) => e.dish_id === res.data.dish_id) || res.data;
        setScannerOpen(false);
        openCount(item);
      } else {
        toast({ title: 'Not in this count', description: res?.error || 'No matching liquor', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Barcode lookup failed', variant: 'destructive' });
    }
  }, [restaurantId, locationId, stocktakeId, data, openCount, toast]);

  const finish = async () => {
    const msg = remaining.length > 0
      ? `${remaining.length} liquor(s) are still un-scanned and will be reported as "not counted". Generate the report now?`
      : 'Generate the variance report and close this stocktake?';
    if (!(await confirm({ description: msg, confirmText: 'Generate report' }))) return;
    try {
      await completeStocktake(restaurantId, locationId, stocktakeId);
      navigate(`/restaurant/${restaurantId}/locations/${locationId}/liquor-tracking/${stocktakeId}/report`);
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to complete', variant: 'destructive' });
    }
  };

  if (loading) return <div className="p-6 text-center text-muted-foreground">Loading stocktake…</div>;
  if (!st) return <div className="p-6 text-center text-muted-foreground">Stocktake not found.</div>;

  return (
    <div className="p-6 w-full">
      <button
        onClick={() => navigate(`/restaurant/${restaurantId}/locations/${locationId}/liquor-tracking`)}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"
      >
        <ChevronLeft className="h-4 w-4" /> Liquor Tracking
      </button>

      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">
            {st.name || 'Stocktake'}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {st.liquor_set_name || 'All liquor'} · {scanned.length} of {expected.length} counted
          </p>
        </div>
        <div className="flex gap-2">
          {!completed && (
            <Button variant="outline" onClick={() => setScannerOpen(true)}>
              <ScanLine className="h-4 w-4 mr-2" /> Scan
            </Button>
          )}
          <Button onClick={finish} disabled={completed}>
            <FileText className="h-4 w-4 mr-2" /> {completed ? 'Completed' : 'Generate report'}
          </Button>
        </div>
      </div>

      {completed && (
        <div className="mb-4 rounded-md bg-success-soft text-success px-4 py-2 text-sm">
          This stocktake is completed and read-only. <button className="underline" onClick={() => navigate(`/restaurant/${restaurantId}/locations/${locationId}/liquor-tracking/${stocktakeId}/report`)}>View the report</button>.
        </div>
      )}

      {/* LEFT: system inventory · RIGHT: scanned items */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base"><Package className="h-4 w-4" /> System inventory ({expected.length})</CardTitle>
            <div className="relative mt-2">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search liquor…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg max-h-[28rem] overflow-auto divide-y">
              {filteredExpected.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">No liquor in this count.</div>
              ) : filteredExpected.map((e) => (
                <button
                  key={e.dish_id}
                  disabled={completed}
                  onClick={() => openCount(e)}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 text-left disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {e.is_counted
                    ? <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                    : <Circle className="h-4 w-4 text-slate-300 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{e.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {e.brand ? `${e.brand} · ` : ''}System: {bottlesText(e.system_full_bottles, e.system_open_ml)}
                    </div>
                  </div>
                  {!completed && <span className="text-xs text-primary shrink-0">{e.is_counted ? 'Edit' : 'Count'}</span>}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base"><CheckCircle2 className="h-4 w-4" /> Scanned items ({scanned.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg max-h-[28rem] overflow-auto divide-y">
              {scanned.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">Nothing counted yet. Scan a bottle or tap a liquor on the left.</div>
              ) : scanned.map((s) => {
                const sysEq = (s.system_full_bottles || 0) + (s.bottle_size_ml > 0 ? (s.system_open_ml / s.bottle_size_ml) : 0);
                const cntEq = (s.counted_full_bottles || 0) + (s.bottle_size_ml > 0 ? (s.counted_open_ml / s.bottle_size_ml) : 0);
                const diff = cntEq - sysEq;
                const tone = Math.abs(diff) < 0.001 ? 'text-muted-foreground' : diff > 0 ? 'text-success' : 'text-destructive';
                return (
                  <div key={s.dish_id} className="flex items-center gap-3 px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{s.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        Counted: {bottlesText(s.counted_full_bottles, s.counted_open_ml)}
                        {s.method === 'barcode' && <span className="ml-1 text-[11px]">· scanned</span>}
                      </div>
                    </div>
                    <div className={`text-xs tabular-nums shrink-0 ${tone}`}>
                      {diff > 0 ? '+' : ''}{diff.toFixed(2)} btl
                    </div>
                    {!completed && (
                      <Button variant="ghost" size="sm" onClick={() => removeScan(s.dish_id)} title="Remove count">
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* BOTTOM: remaining to scan + generate report */}
      <Card className="mt-4">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Circle className="h-4 w-4" /> Left to scan ({remaining.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {remaining.length === 0 ? (
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="text-success flex items-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4" /> Everything has been counted.</p>
              <Button onClick={finish} disabled={completed}><FileText className="h-4 w-4 mr-2" /> Generate report</Button>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-4">
                {remaining.map((r) => (
                  <button
                    key={r.dish_id}
                    disabled={completed}
                    onClick={() => openCount(expected.find((e) => e.dish_id === r.dish_id) || r)}
                    className="px-3 py-1.5 rounded-full border text-sm hover:border-primary/50 hover:bg-slate-50 disabled:opacity-60"
                  >
                    {r.name}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-end gap-4 flex-wrap">
                <Button variant="outline" onClick={finish} disabled={completed}><FileText className="h-4 w-4 mr-2" /> Generate report anyway</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Count entry dialog */}
      <Dialog open={counting !== null} onOpenChange={(o) => !o && setCounting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Count — {counting?.name}</DialogTitle>
            <DialogDescription>
              Enter the physical on-hand. System has {counting ? bottlesText(counting.system_full_bottles, counting.system_open_ml) : ''}.
            </DialogDescription>
          </DialogHeader>
          {counting && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full bottles</Label>
                  <Input type="number" min="0" step="1" value={counting.counted_full_bottles}
                    onChange={(e) => setCounting({ ...counting, counted_full_bottles: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Open bottle (ml)</Label>
                  <Input type="number" min="0" step="1" value={counting.counted_open_ml}
                    onChange={(e) => setCounting({ ...counting, counted_open_ml: e.target.value })} />
                </div>
              </div>
              {counting.bottle_size_ml > 0 && (
                <p className="text-xs text-muted-foreground">Bottle size: {fmtMl(counting.bottle_size_ml)}.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCounting(null)}>Cancel</Button>
            <Button onClick={submitCount} disabled={saving}>{saving ? 'Saving…' : 'Save count'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BarcodeScanner open={scannerOpen} onClose={() => setScannerOpen(false)} onDetected={onBarcode} />
    </div>
  );
}
